import React, { forwardRef, useState } from 'react';
import { Button } from '@/components/button';
import { ChevronDownIcon, ChevronRightIcon, DotsHorizontalIcon, TrashIcon } from '@radix-ui/react-icons';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/dropdown-menu';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/alert-dialog';
import { Handle, Remove } from './item';

export interface Props {
  children: React.ReactNode;
  columns?: number;
  label?: React.ReactNode;
  style?: React.CSSProperties;
  horizontal?: boolean;
  hover?: boolean;
  handleProps?: React.HTMLAttributes<any>;
  scrollable?: boolean;
  shadow?: boolean;
  placeholder?: boolean;
  unstyled?: boolean;
  onClick?(): void;
  onCollapseClick?(): void;
  onRemove?(): void;
  collapsed?: boolean;
}

export const Container = forwardRef<HTMLDivElement, Props>(
  (
    {
      children,
      columns = 1,
      handleProps,
      horizontal,
      hover,
      onClick,
      onCollapseClick,
      onRemove,
      label,
      placeholder,
      style,
      scrollable,
      shadow,
      unstyled,
      collapsed,
      ...props
    }: Props,
    ref
  ) => {
    return (
      <div
        {...props}
        ref={ref}
        style={
          {
            ...style,
            '--columns': columns,
          } as React.CSSProperties
        }
        className={cn(
          'flex flex-col overflow-hidden box-border appearance-none outline-none min-w-[350px] m-2 rounded transition-colors duration-350 ease-in-out bg-secondary/50 border text-base',
          {
            'overflow-visible bg-transparent border-none': unstyled,
            'w-full': horizontal,
            'bg-[rgb(235,235,235)]': hover,
            'justify-center items-center cursor-pointer text-[rgba(0,0,0,0.5)] bg-transparent border-dashed border-[rgba(0,0,0,0.08)] hover:border-[rgba(0,0,0,0.15)]':
              placeholder,
            'overflow-y-auto': scrollable,
            'shadow-lg': shadow,
            'min-h-[200px]': !collapsed,
            // 'min-h-[20px]': collapsed,
          }
        )}
        onClick={onClick}
        tabIndex={onClick ? 0 : undefined}
      >
        {label ? (
          <div
            className={cn(
              'flex pr-3 pl-1 py-1 pr-[8px] items-center justify-between bg-white rounded-t-sm border-b border-[rgba(0,0,0,0.08)]'
            )}
          >
            <div className="flex items-center justify-center space-x-2">
              <Button size="icon" variant="ghost" onClick={onCollapseClick}>
                {collapsed ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
              </Button>
              {label}
            </div>
            <div className={cn('flex')}>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <DotsHorizontalIcon className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="">
                        <TrashIcon className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={onRemove}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
              <Handle {...handleProps} />
            </div>
          </div>
        ) : null}
        {placeholder ? (
          children
        ) : (
          <div
            className={cn('grid gap-1 p-1 grid-cols-[repeat(var(--columns,1),1fr)] list-none m-0', {
              // className={cn('flex flex-col space-y-1', {
              'grid-flow-col': horizontal,
            })}
          >
            {children}
          </div>
        )}
      </div>
    );
  }
);

Container.displayName = 'Container';
